import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, CATEGORY_LABELS } from "@/api";

const EMPTY = { name: "", brand: "", model: "", category: "domestiche", price: "", stock: "", description: "", photos: [] };

function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function MachineForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (initial) {
      setForm({
        name: initial.name, brand: initial.brand, model: initial.model,
        category: initial.category, price: String(initial.price), stock: String(initial.stock),
        description: initial.description, photos: initial.photos || [],
      });
    } else {
      setForm(EMPTY);
    }
  }, [initial]);

  const addFiles = async (files) => {
    for (const file of files) {
      try {
        const dataUrl = await resizeImage(file);
        setForm((f) => ({ ...f, photos: [...f.photos, dataUrl] }));
      } catch {
        toast.error(`Impossibile caricare ${file.name}`);
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.photos.length === 0) {
      toast.error("Aggiungi almeno una foto della macchina");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      stock: parseInt(form.stock, 10) || 0,
    };
    try {
      if (initial) await api.put(`/machines/${initial.id}`, payload);
      else await api.post("/machines", payload);
      toast.success(initial ? "Macchina aggiornata" : "Macchina aggiunta al catalogo");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err, "Salvataggio non riuscito"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="machine-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label htmlFor="m-name">Nome Macchina *</Label>
          <Input id="m-name" required data-testid="machine-input-name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Juki DDL-9000C Industriale" />
        </div>
        <div>
          <Label htmlFor="m-brand">Marca</Label>
          <Input id="m-brand" data-testid="machine-input-brand" value={form.brand}
            onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Es. Singer, Brother, Juki" />
        </div>
        <div>
          <Label htmlFor="m-model">Modello</Label>
          <Input id="m-model" data-testid="machine-input-model" value={form.model}
            onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Es. DDL-9000C-FMS" />
        </div>
        <div>
          <Label>Categoria</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="machine-select-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                <SelectItem key={v} value={v} data-testid={`machine-category-${v}`}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="m-price">Prezzo (€) *</Label>
            <Input id="m-price" type="number" min="0" step="0.01" required data-testid="machine-input-price"
              value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="m-stock">Disponibilità (pz)</Label>
            <Input id="m-stock" type="number" min="0" step="1" data-testid="machine-input-stock"
              value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
          </div>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="m-desc">Descrizione</Label>
          <Textarea id="m-desc" rows={3} data-testid="machine-input-description" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Caratteristiche, stato, accessori inclusi..." />
        </div>
      </div>

      <div>
        <Label>Foto della macchina *</Label>
        <div className="flex flex-wrap gap-3 mt-2">
          {form.photos.map((p, i) => (
            <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-slate-200 group">
              <img src={p} alt="" className="w-full h-full object-cover" />
              <button type="button" data-testid={`machine-photo-remove-${i}`}
                onClick={() => setForm({ ...form, photos: form.photos.filter((_, j) => j !== i) })}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-rose-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button type="button" data-testid="machine-photo-upload-button" onClick={() => fileRef.current?.click()}
            className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 text-slate-400 hover:border-sky-400 hover:text-sky-600 flex flex-col items-center justify-center gap-1 transition-colors">
            <ImagePlus className="w-6 h-6" />
            <span className="text-[10px] font-medium">Carica foto</span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="machine-photo-file-input"
            onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }} />
        </div>
        <div className="flex gap-2 mt-3">
          <Input data-testid="machine-photo-url-input" value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
            placeholder="...oppure incolla l'URL di una foto" className="text-sm" />
          <Button type="button" variant="outline" data-testid="machine-photo-url-add"
            onClick={() => { if (urlInput.trim()) { setForm({ ...form, photos: [...form.photos, urlInput.trim()] }); setUrlInput(""); } }}>
            Aggiungi
          </Button>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={saving} data-testid="machine-form-submit"
          className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-8">
          {saving ? "Salvataggio..." : initial ? "Salva Modifiche" : "Aggiungi Macchina"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="machine-form-cancel" className="rounded-full">
          Annulla
        </Button>
      </div>
    </form>
  );
}
